'use client';
import React, {
  useCallback,
  useMemo,
  useState,
  useRef,
  useLayoutEffect,
} from 'react';
import { ApiAttribute } from '@context/openapi/types';
import InputDescription from '@components/ui/DynamicForm/InputDescription';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@components/components/ui/dropdown-menu';
import { Badge } from '@components/components/ui/badge';
import { cn } from '@components/lib/utils';
import { ChevronDown } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@components/components/ui/tooltip';
interface MultiSelectDropdownProps {
  field: ApiAttribute;
  value: string | number;
  onChange: (field: ApiAttribute, value: string) => void;
  options: string[];
  placeholder?: string;
  onHelpVisible?: (visible: boolean) => void;
  disabled?: boolean;
}

function useFitItemsInOneLine(
  values: string[],
  triggerRef: React.RefObject<HTMLElement | null>,
  badgeWidth = 36
) {
  const measureRef = useRef<HTMLSpanElement>(null);
  const [visibleCount, setVisibleCount] = useState(1);
  const [triggerWidth, setTriggerWidth] = useState<number | null>(null);

  useLayoutEffect(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const updateWidth = () => {
      setTriggerWidth(trigger.clientWidth);
    };

    updateWidth();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => {
        updateWidth();
      });
      observer.observe(trigger);

      return () => {
        observer.disconnect();
      };
    }

    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [triggerRef]);

  useLayoutEffect(() => {
    const trigger = triggerRef.current;
    const measure = measureRef.current;

    if (!trigger || !measure || !triggerWidth) return;

    const availableWidth = triggerWidth - badgeWidth - 32;

    let used = 0;
    let count = 0;

    for (let i = 0; i < values.length; i++) {
      const v = values[i];
      measure.textContent = i === values.length - 1 ? v : v + ', ';
      const width = measure.offsetWidth;

      if (used + width <= availableWidth) {
        used += width;
        count++;
      } else {
        break;
      }
    }

    setVisibleCount(count > 0 ? count : 1);
  }, [values, triggerWidth, badgeWidth, triggerRef]);

  return { visibleCount, measureRef };
}

export const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  field,
  value,
  onChange,
  options,
  placeholder = 'Select values',
  onHelpVisible,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selectedValues = useMemo(() => {
    if (!value) return [];
    const valueStr = String(value);

    if (valueStr.startsWith('[') && valueStr.endsWith(']')) {
      try {
        const parsed = JSON.parse(valueStr);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return valueStr
          .replace(/^\[|\]$/g, '')
          .split(',')
          .map((v) => v.trim().replace(/^"|"$/g, ''))
          .filter(Boolean);
      }
    }

    return valueStr
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
  }, [value]);

  const handleToggle = useCallback(
    (option: string, checked: boolean) => {
      const newSelected = checked
        ? [...selectedValues, option]
        : selectedValues.filter((v) => v !== option);

      const newValue = newSelected.join(',');
      onChange(field, newValue);
    },
    [field, onChange, selectedValues]
  );

  const { visibleCount, measureRef } = useFitItemsInOneLine(
    selectedValues,
    triggerRef
  );

  const visibleItems = selectedValues.slice(0, visibleCount);
  const remainingCount = selectedValues.length - visibleItems.length;

  // ⭐ NEW DISPLAY LOGIC (Always append ", ..." if items are hidden)
  const displayText = useMemo(() => {
    if (selectedValues.length === 0) return placeholder;

    const base = visibleItems.join(', ');

    if (remainingCount > 0) {
      return base + ', ...';
    }

    return base;
  }, [visibleItems, remainingCount, selectedValues, placeholder]);

  return (
    <div className="relative flex-1 w-full min-w-0">
      <span
        ref={measureRef}
        className="absolute invisible whitespace-nowrap"
        aria-hidden="true"
      />

      <div className="ml-auto relative overflow-visible flex items-center gap-1 justify-between w-full z-10 opacity-100">
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
          <TooltipProvider>
            <Tooltip delayDuration={100}>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <button
                    ref={triggerRef}
                    type="button"
                    className={cn(
                      'h-6 pr-6 pl-2 w-full border-l border-form-border cursor-pointer fs-exclude',
                      'bg-white text-left flex items-center gap-2 min-w-0 overflow-hidden',
                      selectedValues.length === 0
                        ? 'text-xs text-neutral-400 py-1'
                        : 'text-sm',
                      'focus-visible:outline-shade-aqua-300'
                    )}
                    disabled={disabled}
                  >
                    <span
                      className={cn(
                        'truncate min-w-0 flex-1 overflow-hidden',
                        selectedValues.length === 0 && 'text-neutral-400'
                      )}
                    >
                      {displayText}
                    </span>

                    {remainingCount > 0 && (
                      <div className="shrink-0 flex items-center justify-center">
                        <Badge
                          variant="secondary"
                          className="text-xs px-1.5 py-0 h-4 flex items-center"
                        >
                          +{remainingCount}
                        </Badge>
                      </div>
                    )}

                    <ChevronDown className="h-4 w-4 text-gray-500 pointer-events-none shrink-0" />
                  </button>
                </DropdownMenuTrigger>
              </TooltipTrigger>

              {selectedValues.length > 0 && (
                <TooltipContent side="right" className="max-w-[400px]">
                  <p className="whitespace-pre-wrap">
                    {selectedValues.join(', ')}
                  </p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>

          <DropdownMenuContent
            className="max-h-60 overflow-y-auto min-w-[180px]"
            align="start"
          >
            {options.map((option) => {
              const isSelected = selectedValues.includes(option);
              return (
                <DropdownMenuCheckboxItem
                  key={option}
                  checked={isSelected}
                  onCheckedChange={(checked) => handleToggle(option, checked)}
                  onSelect={(e) => e.preventDefault()}
                >
                  {option}
                </DropdownMenuCheckboxItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
        <InputDescription
          description={field.description}
          onHelpVisible={onHelpVisible || (() => {})}
        />
      </div>
    </div>
  );
};

export default MultiSelectDropdown;
